import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Tooltip, Icon, Button, Spin, Divider, message } from 'antd';
import { Link } from 'react-router-dom';
import startCase from 'lodash-es/startCase';

import { ListTable } from '@components/table';
import { Tag } from '@components/common';

import BookingActions from '@redux/BookingRedux';
import {
  transformAddonKeyToTitle,
  ADDON_TITLES,
  transformStripePrice
} from '@services/transform';

import API from '@utils/api';
import history from '@utils/history';
import { getErrors } from '@utils/error';
import { formatDate } from '@utils/date';

import { COLORS } from '@theme/styles';
import './RequestsTable.less';

const REQUEST_PAGE_SIZES = [25, 50, 100, 250];

function RequestsTable(props) {
  const { setBookingList } = props;

  const [actionItem, setActionItem] = useState(false);
  const [extraFlag, setExtraFlag] = useState(false);

  const handleMarkComplete = async bookingId => {
    setActionItem(bookingId);

    try {
      await API.markRequestComplete(bookingId);
      setExtraFlag(!extraFlag);
    } catch (err) {
      const error = getErrors(err);
      message.error(error.message);
    }
    setActionItem(false);
  };

  const handleMarkCancel = async bookingId => {
    setActionItem(bookingId);

    try {
      await API.markRequestCanceled(bookingId);
      setExtraFlag(!extraFlag);
    } catch (err) {
      const error = getErrors(err);
      message.error(error.message);
    }
    setActionItem(false);
  };

  const filterDefinitions = [
    {
      key: 'serviceDate',
      component: 'DateSelect',
      label: 'Date of Service',
      width: 4
    },
    {
      key: 'status',
      component: 'SimpleSelect',
      options: ['cleaned', 'cleaning', 'confirmed', 'canceled'],
      label: 'Status',
      width: 4
    },
    {
      key: 'regionID',
      component: 'RegionSearch',
      width: 4
    },
    {
      key: 'serviceType',
      component: 'SimpleSelect',
      options: ['hotel-style', 'deep-clean'],
      label: 'Service',
      width: 4
    },
    {
      key: 'additionalService',
      component: 'SimpleSelect',
      options: Object.values(ADDON_TITLES),
      label: 'Additional Services',
      width: 8
    },
    {
      key: 'userID',
      component: 'NameSearch',
      fetchAPI: API.fetchClients,
      dataField: 'members',
      valueKey: 'userID',
      label: 'Client Name',
      width: 4,
      optionExtractor: item => ({
        text: item.firstName + ' ' + item.lastName,
        value: item.userID
      })
    },
    {
      key: 'userID1',
      component: 'NameSearch',
      fetchAPI: API.fetchClients,
      fetchKey: 'email',
      dataField: 'members',
      valueKey: 'userID',
      labelKey: 'email',
      label: 'Client Email',
      width: 6
    },
    {
      key: 'propertyID',
      component: 'NameSearch',
      fetchAPI: API.fetchProperties,
      dataField: 'properties',
      valueKey: 'propertyID',
      label: 'Property Name',
      width: 6
    },
    {
      key: 'city',
      component: 'CitySearch',
      width: 4
    },
    {
      key: 'state',
      component: 'StateSearch',
      width: 4
    }
  ];

  const dataColumns = [
    {
      title: 'Date of Service',
      dataIndex: 'serviceDate',
      render: value => <>{formatDate(value, true)}</>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      sorter: true
    },
    {
      title: 'Time Frame',
      dataIndex: 'timeframe',
      sorter: true
    },
    {
      title: 'Service',
      dataIndex: 'serviceType',
      render: value => value && startCase(value)
    },
    {
      title: 'Additional Services',
      dataIndex: 'additionalService',
      render: value =>
        value && (
          <ul style={{ padding: 0, listStyleType: 'circle' }}>
            {value.map((item, index) => {
              return (
                <>
                  {index !== 0 && <Divider style={{ margin: '6px 0' }} />}
                  <li key={item}>{transformAddonKeyToTitle(item)}</li>
                </>
              );
            })}
          </ul>
        )
    },
    {
      title: 'Additional Instructions',
      dataIndex: 'notes',
      render: value =>
        value && (
          <Tooltip title={value}>
            <Icon type="info-circle" theme="filled" />
          </Tooltip>
        ),
      sorter: true
    },
    {
      title: 'Internal Notes',
      dataIndex: 'internalNotes',
      render: value =>
        value && (
          <Tooltip title={value}>
            <Icon type="info-circle" theme="filled" />
          </Tooltip>
        ),
      sorter: true
    },
    {
      title: 'Client Name',
      dataIndex: 'name',
      render: (value, row) => (
        <>
          <Link to={`/clients/${row.userID}`}>
            {row.firstName} {row.lastName}{' '}
          </Link>
        </>
      ),
      sorter: true
    },
    {
      title: 'Property Name',
      dataIndex: 'propertyName',
      render: (value, row) => (
        <Link to={`/properties/${row.propertyID}`}>{value}</Link>
      ),
      sorter: true
    },
    {
      title: 'Building Name',
      dataIndex: 'buildingName'
    },
    {
      title: 'Apartment No.',
      dataIndex: 'unitNumber',
      sorter: true
    },
    {
      title: 'No. of Beds',
      dataIndex: 'bedroomsCount',
      sorter: true
    },
    {
      title: 'Region',
      dataIndex: 'regionName'
    },
    {
      title: 'City',
      dataIndex: 'city',
      sorter: true
    },
    {
      title: 'Pets',
      dataIndex: 'hasPets',
      render: value => (value ? <Tag content="YES" color={COLORS.cyan} /> : ''),
      sorter: true
    },
    {
      title: 'Price',
      dataIndex: 'price',
      render: value => <>${transformStripePrice(value)}</>
    },
    {
      title: 'VIP',
      dataIndex: 'isVIP',
      render: value => (value ? <Tag content="VIP" color={COLORS.cyan} /> : ''),
      sorter: true
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (noValue, row) => {
        const { status } = row;
        const showComplete = status !== 'cleaned' && status !== 'canceled';
        const showCancel = status == 'cleaning';

        const isActionItem = actionItem === row.bookingID;
        return (
          <div className="flex vertical">
            <Button
              type="primary"
              style={{ marginBottom: 8 }}
              disabled={isActionItem}
              onClick={() => {
                history.push(`/requests/${row.bookingID}`);
              }}
            >
              Details
            </Button>
            {showComplete && (
              <Button
                type="ghost"
                disabled={isActionItem}
                style={{ color: 'green', marginBottom: 8 }}
                onClick={() => {
                  handleMarkComplete(row.bookingID);
                }}
              >
                {isActionItem ? <Spin /> : 'Complete'}
              </Button>
            )}
            {showCancel && (
              <Button
                type="ghost"
                disabled={isActionItem}
                style={{ color: 'red' }}
                onClick={() => {
                  handleMarkCancel(row.bookingID);
                }}
              >
                {isActionItem ? <Spin /> : 'Cancel'}
              </Button>
            )}
          </div>
        );
      }
    }
  ];

  const filterRowColor = record => {
    const { status } = record;
    return `${status}-row`;
  };

  return (
    <div className="table-view requests-table">
      <ListTable
        extraFlag={extraFlag}
        columns={dataColumns}
        dataId="bookingID"
        filterDefinitions={filterDefinitions}
        pageSizes={REQUEST_PAGE_SIZES}
        fetchAPI={API.fetchRequests}
        dataFields="bookings"
        defaultFilter={{}}
        defaultPageSize={100}
        setCache={setBookingList}
        rowClassName={filterRowColor}
      />
    </div>
  );
}

RequestsTable.propTypes = {
  setBookingList: PropTypes.func
};

const mapDispatchToProps = dispatch => ({
  setBookingList: list => {
    dispatch(BookingActions.setBookingList(list));
  }
});

export default connect(
  null,
  mapDispatchToProps
)(RequestsTable);
